import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Client-side "show the translation keys" debug mode. When enabled, every
 * user-facing string is replaced by its dotted message key (e.g. the label
 * "Speichern" renders as "Common.save"), which makes it trivial to find which
 * key backs a given piece of UI and to spot hard-coded text that bypasses
 * next-intl entirely (it stays as real words).
 *
 * Unlike the server-side `HA_PSEUDO_LOC` glyph mode (see `src/i18n/request.ts`),
 * this toggles instantly at runtime with no reload — see `I18nDebugToggle` for
 * the `Ctrl+Alt+K` shortcut. State is persisted to localStorage so it survives
 * navigation and reloads.
 */
type I18nDebugState = {
    enabled: boolean;
    toggle: () => void;
    setEnabled: (enabled: boolean) => void;
};

export const useI18nDebug = create<I18nDebugState>()(
    persist(
        (set) => ({
            enabled: false,
            toggle: () => set((s) => ({ enabled: !s.enabled })),
            setEnabled: (enabled) => set({ enabled }),
        }),
        { name: "ha-i18n-debug" },
    ),
);

type Messages = Record<string, unknown>;

/**
 * Recursively replace every leaf value in a next-intl messages tree with its
 * fully-qualified dotted key path. Nested objects become their namespaced
 * paths; array entries get an index suffix (`Foo.bar.0`). ICU placeholders in
 * the original values are dropped, which is fine — next-intl ignores unused
 * interpolation args and rich-text tag handlers, so `t`/`t.rich` still render.
 */
export function keyifyMessages(messages: Messages, prefix = ""): Messages {
    const out: Messages = {};
    for (const [key, value] of Object.entries(messages)) {
        const path = prefix ? `${prefix}.${key}` : key;
        if (Array.isArray(value)) {
            out[key] = value.map((_, i) => `${path}.${i}`);
        } else if (value && typeof value === "object") {
            out[key] = keyifyMessages(value as Messages, path);
        } else {
            out[key] = path;
        }
    }
    return out;
}
